/* eslint-disable import/no-anonymous-default-export */
import React, { useRef, useLayoutEffect, useState, useCallback, useEffect } from "react";
import styled from "styled-components";
import AppointmentCard from "./appointmentCard";
import Axios from "axios";
import { findIndex, find } from "lodash";
import {
  isAfter,
  isBefore,
  isEqual,
  addHours,
  format,
  startOfToday,
  addMinutes,
  parseISO,
} from "date-fns";
import { Avatar } from "components/avatar";
import { useQuery } from "react-query";
import useQueryString from "hooks/useQueryString";
import useWindowDimensions from "hooks/useWindowDimensions";
import DroppableCell from "./DailyCell";
import { useTranslation } from "react-i18next";
import { convertFromUTC, convertToUTC } from "utils/utcConversion";
import { useAuthContext } from "contexts/auth";
import { extractLocations } from "views/calendar/utils/extractLocations";
import { locationsToDistances } from "views/calendar/utils/locationsToDistances";
import { useLocationContext } from "views/calendar/hooks/useLocationContext";
import extractLocationsDaily from "./utils/extractLocationsDaily";
import { SearchCalendar } from "views/calendar/utils/searchCalendar";
import { utcToZonedTime } from "date-fns-tz";
import { persistCookie, getCookie } from "utils/cookieStorage";
import { useModalContext } from "components/modal";

const DAILY_SCROLL_START_DEFAULT = 7;

const normalizeDailyScrollStartHour = (hour) => {
  let h = Math.round(Number(hour));
  if (Number.isNaN(h)) h = DAILY_SCROLL_START_DEFAULT;
  return Math.min(23, Math.max(0, h));
};

const readDailyScrollStartHourFromCookie = () => {
  try {
    const saved = getCookie("dailyTimeScrollStart");
    if (saved !== undefined && saved !== null) {
      return normalizeDailyScrollStartHour(saved);
    }
    const legacy = getCookie("dailyTimeframe");
    if (legacy && typeof legacy === "object" && legacy.start != null) {
      return normalizeDailyScrollStartHour(legacy.start);
    }
  } catch {
    // ignore malformed cookie values
  }
  return DAILY_SCROLL_START_DEFAULT;
};

const persistDailyScrollStartHour = (hour) => {
  const v = normalizeDailyScrollStartHour(hour);
  persistCookie("dailyTimeScrollStart", v);
  return v;
};

const formatDailyHourOption = (hour) => {
  if (hour === 0) return "12:00 am";
  if (hour === 12) return "12:00 pm";
  if (hour < 12) return `${hour}:00 am`;
  return `${hour - 12}:00 pm`;
};

const dailyHourSelectOptions = Array.from({ length: 24 }, (_, h) => (
  <option key={h} value={h} className="font-normal">
    {formatDailyHourOption(h)}
  </option>
));

const CalendarCon = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: auto;
  height: 100%;
  align-items: flex-start;
  align-self: flex-start;
  font-size: 0.9rem;
  width: 100%;
  box-shadow: 0 0 10px -5px black;
`;

const FirstRow = styled.div`
  background: white;
  height: 50px;
  display: flex;
  flex: 0 0 auto;
  flex-direction: row;
  position: sticky;
  font-weight: bold;
  top: 0;
  z-index: 2;
  border-bottom: 1px solid black;
  > div {
    border-top: 1px solid #eceff1;
    border-left: 1px solid #e5e7ec;
    display: flex;
    align-items: flex-end;
    justify-content: flex-start;
    &:nth-child(2n) {
      border-left: 1px solid #eceff1;
    }
  }
`;

const FirstCol = styled.div`
  background: white;
  position: sticky;
  width: 150px;
  flex: 0 0 auto;
  left: 0;
  text-align: left;
  padding: 5px;
  font-weight: bold;
  border-left: 1px solid #cfd8dc;
  border-right: 1px solid black;
  border-top: 1px solid #eceff1;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  overflow: hidden;
`;

const Cell = styled.div`
  width: 40px;
  flex: 0 0 auto;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
`;

const AssigneeRow = styled.div`
  display: flex;
  flex-direction: row;
  height: 69px;
  overflow: hidden;
  flex: 0 0 auto;
  border-top: 1px solid #eceff1;
  position: relative; /* This ensures that the pseudo-elements are positioned correctly */

  &:after {
    content: "";
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    border-right: 1px solid #cfd8dc;
    z-index: 1;
  }
`;

const formatHourLabel = (hour) => {
  if (hour === 0) return "12am";
  if (hour === 12) return "12pm";
  if (hour < 12) return `${hour}am`;
  return `${hour - 12}pm`;
};

export default function ({
  date,
  toolTipModal,
  handleToolTip,
  refreshStatus,
  refreshPage,
  appointmentMoved,
  filterProps,
  search,
  setSearch,
}) {
  const { t } = useTranslation();
  const tBase = "views.calendar.containers.daily.dailyContainer";
  const tr = (key) => t(`${tBase}.${key}`);

  const [dailyScrollStartHour, setDailyScrollStartHour] = useState(() =>
    readDailyScrollStartHourFromCookie()
  );

  const handleDailyScrollStartChange = (e) => {
    const v = persistDailyScrollStartHour(e.target.value);
    setDailyScrollStartHour(v);
  };

  const { timezone } = useAuthContext();
  const { setMaxDistance } = useLocationContext();
  const { modal } = useModalContext();

  let height = -19;

  const dimension = useWindowDimensions();
  const calendarScrollRef = useRef(null);
  const scrollLeftRef = useRef(null);
  const scrollAnchorRef = useRef(null);
  const prevHourColumnWidthRef = useRef(null);
  const prevModalOpenRef = useRef(false);

  let parseDate = date ? new Date(date) : new Date();
  let formatDate = format(parseDate, "yyyy-MM-dd");

  const queryString = useQueryString();

  const query = useQuery(
    ["appointment", "daily", formatDate, queryString.search],
    async () =>
      await Axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/appointment/all`,
        {
          params: {
            date: formatDate,
            grid: "daily",
            filters: queryString.search.division,
          },
        }
      ).then((res) => res.data),
    {
      keepPreviousData: true,
      refetchInterval: 60000,
    }
  );
  
  const groupAppointmentsByRow = (array) => {
    return array.reduce(
      (prev, current) => {
        if (!current) return prev;

        let {
          start_time: currentStart,
          end_time: currentEnd,
          start_time_utc: currentStartUtc,
          end_time_utc: currentEndUtc,
          duration,
        } = current;

        let startLocation;

        // if (currentStart) {
        //   startLocation = new Date(currentStart);
        // } else if (currentEnd) {
        //   startLocation = new Date(currentEnd);
        // } else {
        //   startLocation = new Date(addHours(startOfToday(), 7));
        // }

        // if (currentStartUtc) {
        //   startLocation = new Date(currentStartUtc); // Prefer UTC start time
        // } else if (currentEndUtc) {
        //   startLocation = new Date(currentEndUtc); // Use UTC end time as fallback
        // } else if (currentStart) {
        //   startLocation = new Date(currentStart);
        // } else if (currentEnd) {
        //   startLocation = new Date(currentEnd); // Use local end time as fallback
        // } else {
        //   startLocation = new Date(addHours(startOfToday(), 7)); // Default if no times available
        // }

        if (currentStartUtc) {
          // parse the UTC timestamp and shift into companyTZ
          const utcDate = parseISO(currentStartUtc);
          startLocation = utcToZonedTime(utcDate, timezone);
        } else if (currentEndUtc) {
          const utcDate = parseISO(currentEndUtc);
          startLocation = utcToZonedTime(utcDate, timezone);
        } else if (currentStart) {
          startLocation = new Date(currentStart);      // if you ever mix in local-times
        } else if (currentEnd) {
          startLocation = new Date(currentEnd);
        } else {
          startLocation = utcToZonedTime(
            addHours(startOfToday(), 7),
            timezone
          );
        }

        const endLocation = addMinutes(startLocation, duration || 120);

        let index = findIndex(prev, (list) => {
          return (
            findIndex(list, ({ start, end }) => {
              return !(
                isEqual(endLocation, start) ||
                isBefore(endLocation, start) ||
                isAfter(startLocation, end) ||
                isEqual(startLocation, end)
              );
            }) === -1
          );
        });

        index !== -1
          ? prev[index].push({
              ...current,
              start: startLocation,
              end: endLocation,
            })
          : prev.push([{ ...current, start: startLocation, end: endLocation }]);

        return prev;
      },
      [[]]
    );
  };

  const formattedData = query.data?.map(({ appointment, assignee, id }) => {
    const searchLower = search?.toLowerCase?.() || "";
    // Filter unique appointments by id
    const uniqueAppointments = appointment
    ? appointment
        // Filter out duplicates by id
        .filter(
          (a, index, self) =>
            index === self.findIndex((b) => b.id === a.id)
        )
        // Filter by search 
        .filter((a) => {
          // return searching;
          const addressObj = a.address_obj;
          const address =
            `${addressObj?.address || ""} ${addressObj?.city || ""} ${addressObj?.state || ""} ${addressObj?.zipcode || ""}`.trim();

          const addressMatch = address.toLowerCase().includes(searchLower);

          return addressMatch || SearchCalendar(a, [assignee], search);
        })
    : [];
    return {
      assignee,
      id,
      // Then group the unique appointments
      data: uniqueAppointments.length
        ? groupAppointmentsByRow(
            uniqueAppointments
            // .sort((a, b) => new Date(a.start_time_utc) - new Date(b.start_time_utc))
            .sort((a, b) => {
              const aLocal = utcToZonedTime(parseISO(a.start_time_utc), timezone);
              const bLocal = utcToZonedTime(parseISO(b.start_time_utc), timezone);
              return aLocal.getTime() - bLocal.getTime();
            })
          )
        : [],
    };
  });

  const cellWidth = (dimension.width - 185) / 24;
  const hourColumnWidth = cellWidth * 2;

  const restoreSavedScroll = useCallback(() => {
    const el = calendarScrollRef.current;
    const saved = scrollLeftRef.current;
    if (!el || saved == null || hourColumnWidth <= 0) return;
    if (Math.abs(el.scrollLeft - saved) > 1) {
      el.scrollLeft = saved;
    }
  }, [hourColumnWidth]);

  const handleCalendarScroll = useCallback((e) => {
    scrollLeftRef.current = e.currentTarget.scrollLeft;
  }, []);

  // Apply default scroll only when the date or "Default View Time" dropdown changes.
  useLayoutEffect(() => {
    const el = calendarScrollRef.current;
    if (!el || hourColumnWidth <= 0) return;

    const anchorKey = `${formatDate}:${dailyScrollStartHour}`;
    if (scrollAnchorRef.current === anchorKey) return;

    scrollAnchorRef.current = anchorKey;
    const hour = normalizeDailyScrollStartHour(dailyScrollStartHour);
    const nextScrollLeft = hour * hourColumnWidth;
    el.scrollLeft = nextScrollLeft;
    scrollLeftRef.current = nextScrollLeft;
    prevHourColumnWidthRef.current = hourColumnWidth;
  }, [dailyScrollStartHour, formatDate, hourColumnWidth]);

  // Keep the same time visible when column width changes (window resize, scrollbar toggle).
  useLayoutEffect(() => {
    const el = calendarScrollRef.current;
    if (!el || hourColumnWidth <= 0) return;

    const prevWidth = prevHourColumnWidthRef.current;
    if (
      prevWidth != null &&
      prevWidth > 0 &&
      prevWidth !== hourColumnWidth &&
      scrollLeftRef.current != null
    ) {
      const scaled = (scrollLeftRef.current / prevWidth) * hourColumnWidth;
      scrollLeftRef.current = scaled;
      el.scrollLeft = scaled;
    }
    prevHourColumnWidthRef.current = hourColumnWidth;
  }, [hourColumnWidth]);

  // Modal open/close toggles body overflow and can reset nested horizontal scroll.
  useLayoutEffect(() => {
    const wasOpen = prevModalOpenRef.current;
    const isOpen = Boolean(modal);

    if (!wasOpen && isOpen) {
      const el = calendarScrollRef.current;
      if (el) scrollLeftRef.current = el.scrollLeft;
    }

    if (wasOpen && !isOpen) {
      restoreSavedScroll();
      requestAnimationFrame(restoreSavedScroll);
    }

    prevModalOpenRef.current = isOpen;
  }, [modal, restoreSavedScroll]);

  // onAfterClose runs after the close animation and can clobber scroll after layout effects.
  useEffect(() => {
    const handleModalAfterClose = () => {
      restoreSavedScroll();
      requestAnimationFrame(restoreSavedScroll);
    };

    window.addEventListener("eserv-modal-after-close", handleModalAfterClose);
    return () =>
      window.removeEventListener("eserv-modal-after-close", handleModalAfterClose);
  }, [restoreSavedScroll]);

  // Recover if scroll was clobbered while the modal is closed.
  useLayoutEffect(() => {
    if (prevModalOpenRef.current) return;
    restoreSavedScroll();
  });

  const populateCalendar = formattedData?.map(
    ({ assignee, data, id }, assigneeIndex) => {
      if (data.length === 0) {
        height += 69;
      }

      if (formattedData) {
      
        const distances = locationsToDistances(
          extractLocationsDaily(formattedData)
        );
        const max = Math.max(...distances);
        setMaxDistance(max);
      }
      return (
        <React.Fragment key={id}>
          <Row
            style={{
              background: !assignee
                ? "#FFEBEE"
                : assigneeIndex % 2 === 1
                ? "#F4F9FD"
                : "white",
            }}
          >
            <FirstCol
              style={{
                background: !assignee
                  ? "#FFEBEE"
                  : assigneeIndex % 2 === 1
                  ? "#F4F9FD"
                  : "white",
              }}
            >
              {assignee ? (
                <Avatar showLabel={true} size="small" user_id={id}>
                  {assignee}
                </Avatar>
              ) : (
                <>
                  Unassigned
                </>
              )}
            </FirstCol>
            <Content>
              {new Array(data?.length || 1).fill(0).map((_, rowIdx) => {
                let border =
                  rowIdx === 0
                    ? "!border-slate-400 !border-t"
                    : rowIdx === data.length
                    ? "!border-b !border-slate-400"
                    : "";

                return (
                  <AssigneeRow className={border}>
                    {new Array(24).fill("").map(
                      (_, cellIdx) => (
                        <Cell
                          key={id + "-" + cellIdx}
                          style={{
                            width: hourColumnWidth + "px",
                            borderRight:
                              cellIdx < 23 ? "1px solid #cfd8dc" : "none",
                          }}
                        ></Cell>
                      ),
                      {
                        /*
                     UNCOMMENT THIS TO: start working on the daily drag and drop, make sure to remove comments as well and cell above
                     note: unscheduled/partially complete also don't work
                     */
                      },
                      {
                        /* (
                      <DroppableCell
                        key={id + "-" + cellIdx}
                        {...{ appointmentMoved }}
                        formatDate={formatDate}
                        newTime={addMinutes(startOfDay(parseDate), cellIdx * 60)} 
                        crewId={id}
                        style={{
                          width: hourColumnWidth + "px",
                          borderRight: cellIdx < 23 ? '1px solid #cfd8dc' : 'none',
                        }}
                      >
                      </DroppableCell>
                    ) */
                      }
                    )}
                  </AssigneeRow>
                );
              })}
            </Content>
          </Row>

          {data?.map((row) => {
            height += 69;

            const appointmentCards = row.map((appointment) => {
              return (
                <AppointmentCard
                  latitude={appointment.latitude}
                  longitude={appointment.longitude}
                  key={id + " " + appointment.id}
                  handleToolTip={handleToolTip}
                  {...{ appointment, toolTipModal, height, cellWidth }}
                  gridStartHour={0}
                  selected={toolTipModal && toolTipModal.id === appointment.id}
                />
              );
            });

            return appointmentCards;
          })}
        </React.Fragment>
      );
    }
  );

  return (
    <CalendarCon ref={calendarScrollRef} onScroll={handleCalendarScroll}>
      <FirstRow>
        <FirstCol
          style={{
            zIndex: 3,
            flexDirection: "column",
            gap: 4,
            alignItems: "flex-start",
            alignSelf: "stretch",
          }}
        >
          <span className="block w-full text-left text-[10px] font-bold leading-tight text-slate-900">
            {tr("Default View Time")}
          </span>
          <select
            className="w-full max-w-full rounded border border-slate-300 bg-white px-1 py-0.5 font-bold"
            value={dailyScrollStartHour}
            onChange={handleDailyScrollStartChange}
          >
            {dailyHourSelectOptions}
          </select>
        </FirstCol>
        {new Array(24).fill("").map((_, h) => (
          <React.Fragment key={`first-row-${h}`}>
            <Cell style={{ width: hourColumnWidth + "px" }}>
              {formatHourLabel(h)}
            </Cell>
          </React.Fragment>
        ))}
      </FirstRow>
      {populateCalendar}
    </CalendarCon>
  );
}
